import { FrameLoop } from "./frameloop";
import { GPSCoordinate } from "./gpscoordinate";

import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { OnEventDispatcher } from "./on-event-dispatcher";

type MarkerGroups = { [key: string]: MarkerGroup };

export class MarkerGroup {
    markers: google.maps.marker.AdvancedMarkerElement[];
    //clusterer:MarkerClusterer;
    constructor(map:any) {
        this.markers = [];
        //this.clusterer = new MarkerClusterer({map:map, markers:[]});
    }
}

export type MapBoundState = {
    zoom:number,
    center:GPSCoordinate
}
export type MapDimension = {
    width:number,
    height:number
}
export class GoogleMaps extends OnEventDispatcher {

    container: JQuery<HTMLElement>;
    mapContainer: JQuery<HTMLElement>;

    options: google.maps.MapOptions;

    map: google.maps.Map;

    mapDimension:MapDimension;

    currentMapState:MapBoundState;
    targetMapState:MapBoundState;
    stateNeedsPush:boolean = false;
    stateIsPushing:boolean = false;

    _lockUserZoom:boolean = false;
    _lockUserPan:boolean = false;

    frameLoop:FrameLoop;

    markerGroups:MarkerGroups = {};

    private _bounds: google.maps.LatLngBounds;

    constructor(container:JQuery<HTMLElement>, options:google.maps.MapOptions) {
        super();

        this.container = container;
        this.options = { ...options, mapId: "7595a62a722e8e40f0e74acc" };
        // When using mapId, styling must be done in the Cloud Console. Remove inline styles to avoid warnings.
        if ((this.options as any).styles) {
            delete (this.options as any).styles;
        }

        this.currentMapState = {
            zoom: 1,
            center: new GPSCoordinate(0,0)
        }
        this.targetMapState = {
            zoom: 1,
            center: new GPSCoordinate(0,0)
        }
        
        this.mapContainer = $("<div class='inner-map-container'></div>").appendTo(this.container);
    }

    public mount(): Promise<void> {
        return new Promise((resolve, reject) => {
            try {
                this.map = new google.maps.Map(this.mapContainer[0], this.options);

                // Wait until the map has rendered once so bounds + container dimensions exist.
                google.maps.event.addListenerOnce(this.map, "idle", () => {
                    this.updateDimensions();
                    this.setupMapListeners();
                    this.setupFrameLoop();
                    resolve();
                });
            } catch (e) {
                reject(e);
            }
        });
    }
    updateDimensions() {
        const w = this.mapContainer.width();
        const h = this.mapContainer.height();
        this.mapDimension = {
            width: w ?? 0,
            height: h ?? 0,
        };
    }

    private setupMapListeners() {
        this.map.addListener("bounds_changed", () => {
            this.onBoundsChanged();
        });
        this.onBoundsChanged();
        
    }
    private setupFrameLoop() {
        this.frameLoop = new FrameLoop();
        this.frameLoop.on("loop", (delta:number) => { this.onFrameLoop(delta); })
    }

    private onFrameLoop(_delta:number) {
        if(this.stateNeedsPush&&!this.stateIsPushing) {
            this.pushState();
        }
    }

    public get bounds():google.maps.LatLngBounds {
        return this._bounds;
    }

    private onBoundsChanged() {
        this._bounds = this.map.getBounds();
        let center = this.map.getCenter();
        this.currentMapState = {
            zoom: this.map.getZoom(),
            center: GPSCoordinate.fromGoogle(center)
        }
        this.updateDimensions();
        this.stateIsPushing = false;
        if(this.stateNeedsPush) {
            this.pushState();
        }
        this.dispatch("bounds_changed");
    }
    private pushState() {
        this.stateIsPushing = true;
        this.stateNeedsPush = false;
        this.map.setValues({
            zoom: this.targetMapState.zoom,
            center: this.targetMapState.center.toGoogle()
        });
    }
    public setZoom(zoom:number) {
        if(this.targetMapState.zoom!=zoom) {
            this.targetMapState.zoom = zoom;
            this.stateNeedsPush = true;
        }
    }
    public setCenter(center:google.maps.LatLngLiteral|GPSCoordinate) {
        if(!(center as GPSCoordinate).isGPSCoordinate) center = new GPSCoordinate(center.lat, center.lng);
        if(this.targetMapState.center.lat!=center.lat||this.targetMapState.center.lng!=center.lng) {
            this.targetMapState.center = center as GPSCoordinate;
            this.stateNeedsPush = true;
        }
    }
    public getZoom():number {
        return this.targetMapState.zoom;
    }
    public getCurrentZoom():number {
        return this.map.getZoom();
    }
    public getCenter():GPSCoordinate {
        return this.targetMapState.center.clone();
    }
    public fitBounds(bounds:google.maps.LatLngBounds, padding:{x:number, y:number} = null) {
        let boundsCenter = bounds.getCenter();
        this.setCenter(new GPSCoordinate(boundsCenter.lat(), boundsCenter.lng()));
        this.setZoom(this.getBoundsZoomLevel(bounds, padding));
    }
    private updateUserInteractionOptions() {
        //gestureHandling: 'none'
        //gestureHandling: 'greedy'
        let opts = {
            //zoom
            zoomControl: false,//!this._lockUserZoom,
            scrollwheel: !this._lockUserZoom,
            disableDoubleClickZoom: this._lockUserZoom,

            //pan
            draggable: !this._lockUserPan,

            //shared
            keyboardShortcuts: (this._lockUserPan||this._lockUserPan)?false:true
        }
        this.map.setOptions(opts);
    }
    public lockZoom():void {
        //console.log("Zoom: lock");
        this._lockUserZoom = true;
        this.updateUserInteractionOptions();
    }
    public unlockZoom():void {
        //console.log("Zoom: unlock");
        this._lockUserZoom = false;
        this.updateUserInteractionOptions();
    }
    public lockPan() {
        //console.log("Pan: lock");
        this._lockUserPan = true;
        this.updateUserInteractionOptions();
    }
    public unlockPan() {
        //console.log("Pan: unlock");
        this._lockUserPan = false;
        this.updateUserInteractionOptions();
    }

    private getBoundsZoomLevel(bounds:google.maps.LatLngBounds, padding:{x:number, y:number}):number {
        const GMAPS_DIM_CONSTANT = 256;
        const ZOOM_MAX = 21;

        if(padding==null) padding={x:0, y:0};
        if(!this.mapDimension) this.updateDimensions();
        const safeHeight = Math.max(256, (this.mapDimension?.height ?? 0) - padding.y);
        const safeWidth = Math.max(256, (this.mapDimension?.width ?? 0) - padding.x);

        let latRad = (lat:number) => {
            var sin = Math.sin(lat * Math.PI / 180);
            var radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
            return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
        }
        let zoom = (mapPx:number, worldPx:number, fraction:number) => {
            return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);
        }

        let ne = bounds.getNorthEast();
        let sw = bounds.getSouthWest();

        let latFraction = (latRad(ne.lat()) - latRad(sw.lat())) / Math.PI;

        let lngDiff = ne.lng() - sw.lng();
        let lngFraction = ((lngDiff < 0) ? (lngDiff + 360) : lngDiff) / 360;

        let latZoom = zoom(safeHeight, GMAPS_DIM_CONSTANT, latFraction);
        let lngZoom = zoom(safeWidth, GMAPS_DIM_CONSTANT, lngFraction);

        return Math.max(1, Math.min(latZoom, lngZoom, ZOOM_MAX));
    }
    

    private placeToPlaceResult(place: any): google.maps.places.PlaceResult {
        // The new Places API uses `google.maps.places.Place`.
        // This adapter keeps the rest of the code working with the older `PlaceResult`-shaped objects.
        const name = place?.displayName?.text ?? place?.displayName ?? place?.name;
        const location = place?.location ?? place?.geometry?.location;
        const formattedAddress = place?.formattedAddress ?? place?.formatted_address;

        return {
            place_id: place?.id ?? place?.place_id,
            name,
            formatted_address: formattedAddress,
            geometry: location ? ({ location } as any) : undefined,
            types: place?.types,
            business_status: place?.businessStatus ?? place?.business_status,
        } as any;
    }

    public async textSearch(search:string, language:string = "sv", region:string = "se", filterTypes:string[] = null, exludeBusinesses:boolean = true):Promise<google.maps.places.PlaceResult[]> {
        let arrIncludesAnyOf = (arr:string[], includes:string[]):boolean => {
            for(let m in includes) {
                let c = includes[m];
                if(arr.includes(c)) return true;
            }
            return false;
        }

        // New Places API (recommended). Requires `await google.maps.importLibrary("places")`.
        const { Place } = await google.maps.importLibrary("places") as google.maps.PlacesLibrary;

        const response = await Place.searchByText({
            textQuery: search,
            language,
            region,
            // Bias results towards current map bounds if available
            locationBias: this.map?.getBounds?.() as any,
            // Keep fields minimal; add more if you need them
            fields: ["id", "displayName", "location", "formattedAddress", "types", "businessStatus"],
        } as any);

        const places = (response as any)?.places ?? [];
        let results: google.maps.places.PlaceResult[] = places.map((p:any) => this.placeToPlaceResult(p));

        // Preserve the old filtering behavior
        let filteredResults:google.maps.places.PlaceResult[] = results;
        if(filterTypes==null && !exludeBusinesses) {
            // no-op
        } else {
            filteredResults = [];
            if(exludeBusinesses) {
                for(let m in results) {
                    let c = results[m];
                    if((c as any).business_status==null && (c as any).businessStatus==null) {
                        filteredResults.push(c);
                    }
                }
                results = filteredResults;
            }
            if(filterTypes!=null) {
                for(let m in results) {
                    let c = results[m];
                    if(c.types && arrIncludesAnyOf(c.types as any, filterTypes)) filteredResults.push(c);
                }
            }
        }

        return filteredResults;
    }
    
    public zoomToWorld():void {
        this.setCenter(new GPSCoordinate(41.94540762994656, 36.877420912333974));
        this.setZoom(1);
    }
    public async getPlaceDetails(place_id:string):Promise<google.maps.places.PlaceResult> {
        const { Place } = await google.maps.importLibrary("places") as google.maps.PlacesLibrary;
        const place = new Place({ id: place_id } as any);

        await (place as any).fetchFields({
            fields: [
                "id",
                "displayName",
                "location",
                "formattedAddress",
                "types",
                "businessStatus"
            ]
        });

        return this.placeToPlaceResult(place);
    }

    public async addMarker(markerOptions:google.maps.MarkerOptions, group:string):Promise<google.maps.marker.AdvancedMarkerElement> {
        if(this.markerGroups[group]==null) this.markerGroups[group] = new MarkerGroup(this.map);

        const { AdvancedMarkerElement } = await google.maps.importLibrary("marker") as google.maps.MarkerLibrary;

        const marker = new AdvancedMarkerElement({
            map: this.map,
            position: markerOptions.position as any,
            title: markerOptions.title,
        } as any);

        this.markerGroups[group].markers.push(marker);
        return marker;
    }
    public hideMarkerGroup(group:string):void {
        for(let m in this.markerGroups[group].markers) {
            this.markerGroups[group].markers[m].map = null;
        }
    }
    public showMarkerGroup(group:string):void {
        for(let m in this.markerGroups[group].markers) {
            this.markerGroups[group].markers[m].map = this.map;
        }
    }
    public removeMarkerGroup(group:string):void {
        this.hideMarkerGroup(group);
        delete this.markerGroups[group];
    }
    public createBounds(coords: ({lat: number;lng: number;}|GPSCoordinate)[]): google.maps.LatLngBounds {
        var bounds = new google.maps.LatLngBounds();
        if(coords==null||coords.length<=0) return bounds;
        for(let m in coords) {
            if((coords[0] as GPSCoordinate).isGPSCoordinate) {
                bounds.extend((coords[m] as GPSCoordinate).toGoogle());
            } else {
                bounds.extend(coords[m]);
            }
        }
        return bounds;
    }
    

    public drawGeom(geom: {type:string, coordinates:Array<any>}):{poly: google.maps.Polygon, bounds:google.maps.LatLngBounds} {
        
        let coordArrays:any[] = [];
        let bounds:google.maps.LatLngBounds = new google.maps.LatLngBounds();

        let parseCoords = (coordArr:Array<Array<number>>):Array<{lat:number,lng:number}> => {
            let gCoords:Array<{lat:number,lng:number}> = [];
            for(let m in coordArr) {
                let c = coordArr[m];
                if(c.length!=2) {
                    //console.log(geom);
                    //console.log(c);
                    throw new Error("Parsing error");
                }
                let latlng = {lng: c[0], lat: c[1]};
                gCoords.push(latlng);
                bounds.extend(latlng);
            }
            return gCoords;
        }
        

        if(geom.type=="MultiPolygon") {
            if(geom.coordinates.length<=1) throw new Error("Parsing error, MultiPolygon has less than 2 coordinate arrays");
            for(let m in geom.coordinates) {
                let c = geom.coordinates[m];
                if(c.length!=1) throw new Error("Parsing error, Polygon inside MultiPolygon does not have one sublist");
                coordArrays.push(parseCoords(c[0]));
            }
        } else {
            if(geom.coordinates.length!=1) {
                //this is a polygon with a hole inside (or this is my best guess..)
                for(let m in geom.coordinates) {
                    coordArrays.push(parseCoords(geom.coordinates[m]));
                }
            } else {
                coordArrays.push(parseCoords(geom.coordinates[0]));
            }
        }

        if(coordArrays.length<=0) return;

        let pathPoly = new google.maps.Polygon({
            paths: coordArrays,
            fillColor: "#FFC107",
            fillOpacity: 0.35,
            geodesic: true,
            strokeColor: "#000000",
            strokeOpacity: 0.1,
            strokeWeight: 1,
        });
        pathPoly.setMap(this.map);

        return {poly: pathPoly, bounds: bounds};
    }

    public static getRouteURL(from:GPSCoordinate, to:GPSCoordinate, steps:Array<GPSCoordinate> = null) {
		let url = "https://www.google.com/maps/dir/?api=1";
        if(from!=null) {
		    url+="&origin="+from.toFixedGPSString();
        }
		url+="&destination="+to.toFixedGPSString();
		url+="&travelmode=walking";
		url+="&dir_action=navigate";
        if(steps!=null) {
            url+="&waypoints=";
            let cnt=0;
            for(let m in steps) {
                if(cnt>0) url+="|";
                url+=steps[m].toFixedGPSString();
                cnt++;
            }
        }
		return url;
	}

    public static init():Promise<void> {
        return new Promise(async (resolve, reject) => {
            try {
                await google.maps.importLibrary("maps");
                await google.maps.importLibrary("places");
                await google.maps.importLibrary("marker");
            } catch(e) {
                return reject(e);
            }
            resolve();
        });
    }
}